import React from "react";
import { Box, Typography, IconButton, Stack, Avatar, Checkbox } from "@mui/material";
import { MoreVert, Bookmark as BookmarkIcon } from "@mui/icons-material";
import { usePaletteColors } from "../hooks/useDarkMode.ts";
import { PaletteColor } from "../types/PaletteColor.ts";
import { FileThumbnail } from "./FileThumbnail.tsx";

interface ItemCardProps {
  title: string;
  subTitle?: string;
  icon: React.ReactNode;
  palette: PaletteColor;
  onClick: () => void;
  onActionClick: (event: React.MouseEvent<HTMLElement>) => void;
  // ▼ 追加: 右側のアイコンを指定可能にする (未指定ならMoreVert)
  actionIcon?: React.ReactNode;
  // ▼ 選択モード用
  isSelectionMode?: boolean;
  isSelected?: boolean;
  // ▼ サムネイル URL (指定時はアイコンの代わりに表示、取得失敗時は icon にフォールバック)
  thumbnailUrl?: string;
  // ▼ ブックマーク済みならカード左上角にリボンを表示
  isBookmarked?: boolean;
}

export const ItemCard: React.FC<ItemCardProps> = ({
  title,
  subTitle,
  icon,
  palette,
  onClick,
  onActionClick,
  actionIcon,
  isSelectionMode = false,
  isSelected = false,
  thumbnailUrl,
  isBookmarked = false,
}) => {
  const { bgColor, accentColor, isDarkMode } = usePaletteColors(palette);

  const cardBaseBg = isDarkMode ? "#1f2937" : "#ffffff";
  const borderColor = isDarkMode ? "#374151" : "#cbd5e1";
  const shadowColor = isDarkMode ? "#000000" : "#94a3b8";
  const titleColor = isDarkMode ? "#f3f4f6" : "#334155";
  const boxShadow = `0px 2px 0px 0px ${shadowColor}`;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        bgcolor: isSelected
          ? (isDarkMode ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.08)")
          : cardBaseBg,
        borderRadius: 3,
        border: `2px solid ${isSelected ? accentColor : borderColor}`,
        boxShadow: boxShadow,
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.1s, box-shadow 0.1s, border-color 0.2s, background-color 0.2s",
        // 1. 全体の縦の長さを短く (80 -> 64)
        height: 68,
        pr: 0,
        "&:active": {
          transform: "translateY(2px)",
          boxShadow: `0px 0px 0px 0px ${shadowColor}`,
        },
        "&:hover": {
          borderColor: isDarkMode ? accentColor : "#94a3b8",
        },
      }}
    >
      {/* ブックマークリボン (カード左上角。上端から垂れ下がる見た目) */}
      {isBookmarked && (
        <BookmarkIcon
          sx={{
            position: "absolute",
            top: -4,
            left: 8,
            fontSize: 20,
            color: "#FFB020",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
      )}
      <Stack
        direction="row"
        alignItems="center"
        // 2. 左アイコンとテキストの距離
        spacing={1.5}
        sx={{ pl: isSelectionMode ? 0.5 : 2, flexGrow: 1, overflow: "hidden" }}
      >
        {isSelectionMode && (
          <Checkbox
            checked={isSelected}
            size="small"
            sx={{
              color: accentColor,
              "&.Mui-checked": { color: accentColor },
              p: 0.5,
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {/* 3. Avatarを廃止し、アイコンを大きく表示 */}
        <Box
          sx={{
            color: accentColor,
            display: "flex",
            alignItems: "center",
            "& svg": { fontSize: 32 }, // アイコンサイズを大きく
          }}
        >
          {thumbnailUrl ? (
            <FileThumbnail src={thumbnailUrl} size={44} fallback={icon} />
          ) : (
            icon
          )}
        </Box>

        <Box
          sx={{
            minWidth: 0,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
              color: titleColor,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              // 4. タイトルとサブタイトルの行間を詰める
              lineHeight: 1.4,
            }}
          >
            {title}
          </Typography>
          {subTitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ lineHeight: 1.2 }} // 行間を詰める
            >
              {subTitle}
            </Typography>
          )}
        </Box>
      </Stack>

      <Box
        sx={{
          position: "relative",
          height: "100%",
          width: 50, // 高さに合わせて少し幅も調整
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ml: 1,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: bgColor,
            clipPath: "polygon(30% 0%, 100% 0%, 100% 100%, 0% 100%)",
            zIndex: 0,
          }}
        />

        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onActionClick(e);
          }}
          size="small"
          sx={{
            zIndex: 1,
            bgcolor: isDarkMode ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.7)",
            color: accentColor,
            "&:hover": {
              bgcolor: isDarkMode ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,1)",
            },
          }}
        >
          {actionIcon ? actionIcon : <MoreVert fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );
};
